import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import {makeStyles} from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import SettingsIcon from '@material-ui/icons/Settings';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import millify from 'millify';

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: 'transparent',
    boxShadow: '0 0 50px rgba(0, 0, 0, 0.2)',
    padding: 0,
    '& .MuiAlert-root': {
      margin: 0,
    },
  },
  insightsWrapper: {display: 'inline-block', position: 'absolute', right: 10},
  trendsBadgeSpan: {
    fontWeight: 'bold',
    fontFamily: 'monospace',
    fontSize: 10,
    top: 7,
  },
});

const insightsMap = {
  inactive: {
    severityLevel: 0,
    severity: () => 'error',
    icon: () => <SettingsIcon style={{color: 'red'}} />,
    description: insight =>
      `Potential Prebid Panel configuration issue - SSP was enabled but generated no impressions for selected period.`,
  },
  new: {
    severityLevel: 1,
    severity: () => 'success',
    icon: () => <NewReleasesIcon style={{color: '#56cb80'}} />,
    description: insight => `Was added on ${insight}`,
  },
  inactiveDays: {
    severityLevel: 2,
    severity: () => 'warning',
    icon: () => <ReportProblemIcon style={{color: 'orange'}} />,
    description: insight => `Days without activity: ${insight}`,
  },
  trend: {
    severityLevel: 3,
    severity: insight => (insight.direction === 'up' ? 'success' : 'warning'),
    icon: insight =>
      insight.direction === 'up' ? (
        <TrendingUpIcon style={{color: '#56cb80'}} />
      ) : (
        <TrendingDownIcon style={{color: 'orange'}} />
      ),
    description: insight =>
      `${
        insight.direction === 'up' ? 'Impressions increased' : 'Impressions declined'
      } by ${Math.abs(insight.value)}%`,
    color: insight => (insight.direction === 'up' ? '#56cb80' : 'orange'),
  },
  stopped: {
    severityLevel: 4,
    severity: () => 'error',
    icon: () => <ReportProblemIcon style={{color: 'red'}} />,
    description: insight => `Stopped bidding after ${insight}`,
  },
};

const Insights = ({insights, onMouseEnter, onMouseLeave, isSourceActive}) => {
  const classes = useStyles();
  if (!insights || Object.keys(insights).length === 0) return null;

  // Get the highest severity insight by looping through the insights and finding the highest severity by checking severity level in insightsMap
  const highestSeverityInsight = Object.keys(insights).reduce((max, key) => {
    return insightsMap[key].severityLevel > insightsMap[max].severityLevel ? key : max;
  }, Object.keys(insights)[0]);

  const highestSeverityInsightIsTrend = highestSeverityInsight === 'trend';
  const badgeContent = highestSeverityInsightIsTrend
    ? `${millify(Math.abs(insights.trend.value), {precision: 0})}%`
    : '';

  const insightData = insights[highestSeverityInsight];
  const config = insightsMap[highestSeverityInsight];

  return (
    <div
      className={classes.insightsWrapper}
      onMouseEnter={() => (isSourceActive ? onMouseEnter(insights) : null)}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip
        title={
          isSourceActive ? (
            <Alert severity={config.severity(insightData)} icon={config.icon(insightData)}>
              {config.description(insightData)}
            </Alert>
          ) : null
        }
        classes={{
          tooltip: classes.tooltip,
        }}
      >
        <Badge
          badgeContent={badgeContent}
          classes={{badge: classes.trendsBadgeSpan}}
          style={{
            color: highestSeverityInsightIsTrend
              ? insightsMap.trend?.color(insights.trend)
              : 'transparent',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          overlap="rectangular"
        >
          {config.icon(insightData)}
        </Badge>
      </Tooltip>
    </div>
  );
};

export default Insights;
